import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { PawPrint } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      const dest = loc.state?.from?.pathname || "/";
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-foreground/15 grid place-items-center backdrop-blur-sm">
              <PawPrint className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-2xl leading-none">Furmigo</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-5xl font-black tracking-tight leading-tight">
            One ERP.<br />Every paw printed.
          </h1>
          <p className="mt-6 text-primary-foreground/80 text-lg leading-relaxed">
            Fast POS, full inventory, customer & pet profiles, vaccination tracker, public bookings — all from a single screen built for daily store life.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-3 max-w-md">
          {["Lightning POS", "Pet profiles", "WhatsApp alerts"].map((s) => (
            <div key={s} className="border border-primary-foreground/20 rounded-lg p-3">
              <div className="text-xs uppercase tracking-widest opacity-70">Feature</div>
              <div className="font-semibold mt-1 text-sm">{s}</div>
            </div>
          ))}
        </div>
        <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-primary-foreground/10" />
        <div className="absolute -top-24 -left-24 w-[360px] h-[360px] rounded-full bg-primary-foreground/10" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">P</div>
            <div className="font-heading font-bold text-xl">Furmigo</div>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Welcome back</h2>
          <p className="text-muted-foreground mt-2">Sign in with your staff account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="email"
                required
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
                required
                className="h-11 mt-1.5"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 text-base" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <Card className="mt-8 p-4 bg-muted/40 border-dashed">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Need an account?</div>
            <div className="mt-2 text-sm text-muted-foreground">
              Contact your store administrator to receive your staff credentials.
            </div>
          </Card>

          <div className="mt-6 text-sm text-muted-foreground">
            Customer? <a href="/book" className="text-primary font-semibold hover:underline" data-testid="public-book-link">Book an appointment</a>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
